import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  useGetVideoIntroDataQuery,
  useGetShowreelQuery,
} from "../../../apis/apiSlice";
import { capturePartnerEvent } from "../../../utils/analytics";

const BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000"; // adjust as needed

const LandingPage = ({ userId }) => {
  const navigate = useNavigate();

  const {
    data: videoIntroData,
    isLoading: isIntroLoading,
    isError: isIntroError,
  } = useGetVideoIntroDataQuery();

  const {
    data: showreelData,
    isLoading: isShowreelLoading,
  } = useGetShowreelQuery();

  const [intro, setIntro] = useState(null);
  const [showreel, setShowreel] = useState(null);
  const [isUserAccess, setIsUserAccess] = useState(false);
  const [noAccessPopup, setNoAccessPopup] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [errorPopup, setErrorPopup] = useState(false);

  useEffect(() => {
    capturePartnerEvent("partner_video_intro_viewed");
  }, []);

  useEffect(() => {
    if (noAccessPopup) {
      capturePartnerEvent("partner_video_intro_access_denied");
    }
  }, [noAccessPopup]);

  useEffect(() => {
    if (isIntroError) {
      setErrorPopup(true);
    } else if (videoIntroData) {
      setIntro(videoIntroData);
    }
  }, [videoIntroData, isIntroError]);

  useEffect(() => {
    if (showreelData) {
      setShowreel(showreelData);
    }
  }, [showreelData]);

  useEffect(() => {
    const fetchAccess = async () => {
      if (!userId) return;
      setIsCheckingAccess(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/pay/checkuseraccess/${userId}/PV_CreateVideoIntro`
        );
        setIsUserAccess(Boolean(res?.data?.hasAccess));
      } catch (e) {
        // optional error handling UI
      } finally {
        setIsCheckingAccess(false);
      }
    };

    fetchAccess();
  }, [userId]);

  const handleCreateStatus = async () => {
    if (!intro) return;

    if (intro.status === "started") {
      navigate("/message");
    } else if (intro.status === "initial") {
      navigate("/preview");
    } else if (intro.status === "completed" && intro.video == null) {
      navigate("/preview");
    } else if (intro.message) {
      if (isUserAccess) {
        setNoAccessPopup(true);
      } else {
        try {
          const token = localStorage.getItem("accessToken");
          const response = await axios.get(
            `${BASE_URL}/viewerlounge/GetAllSingleVideos`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          localStorage.setItem(
            "partnersViewCurrentQuestionNumber",
            (response?.data?.length ?? 0) + 1
          );
          navigate("/message");
        } catch (error) {
          console.error("Error fetching video URLs:", error);
        }
      }
    }
  };

  const handleVideoIntro = () => {
    if (intro?.status === "completed" && intro?.video) {
      navigate("/final");
    } else {
      localStorage.removeItem("partnersViewEditVideoIntro");
      handleCreateStatus();
    }
  };

  const handleEditVideo = () => {
    localStorage.setItem("partnersViewCurrentQuestionNumber", 11);
    localStorage.setItem("partnersViewEditVideoIntro", "true");
    navigate("/preview");
  };

  const isLoading = isIntroLoading || isShowreelLoading || isCheckingAccess;

  return (
    <div className="px-4 py-6">
      {errorPopup && <div>Something went wrong loading your data.</div>}
      {isLoading ? (
        <div>Loading…</div>
      ) : (
        <div>
          <div className="text-gray-600 font-medium text-sm md:text-base leading-relaxed text-justify">
            <div className="hidden md:block">
              <p>
                In the film industry, it is important that Showreel and
                Introduction circulate widely.
              </p>
              <p>
                In the Partners View, you can instantly record your video
                introduction and upload it.
              </p>
              <p>Introduce yourself to the film-making community.</p>
            </div>
            <div className="block md:hidden">
              <ul className="list-disc pl-4">
                <li className="mb-2">
                  In the film industry, it is important that Showreel and
                  Introduction circulate widely.
                </li>
                <li className="mb-2">
                  In the <span className="font-semibold">Partners View</span>,
                  you can instantly record your video introduction and upload
                  it.
                </li>
                <li className="mb-2">
                  Introduce yourself to the film-making community.
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-4 flex flex-col md:flex-row gap-3">
            <button
              className="px-3 py-1 rounded-lg h-8 text-white bg-sky-500"
              onClick={handleVideoIntro}
            >
              {intro?.status === "completed" && intro?.video
                ? "View Video Introduction"
                : "Create Video Introduction"}
            </button>

            {intro?.status === "completed" && intro?.video && (
              <button
                className="px-3 py-1 rounded-lg h-8 text-white bg-sky-500"
                onClick={handleEditVideo}
              >
                Edit Video Introduction
              </button>
            )}

            <button
              className="px-3 py-1 rounded-lg h-8 text-white bg-sky-500"
              onClick={() =>
                navigate("/upload-showreel?upload_showreel=true")
              }
            >
              {showreel?.message ? "Upload Showreel" : "Change Showreel"}
            </button>
          </div>

          {noAccessPopup && (
            <div className="mt-4 text-red-500">
              You don’t have access to create a new video introduction.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LandingPage;

